import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  ArrowRight,
  Shield,
  ShieldCheck,
  ShieldAlert,
  UserCheck,
  Bot,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Globe,
  Lock,
  Users,
  Award,
  BadgeCheck,
  Fingerprint,
  Scale
} from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

export default function FraudPreventionPage() {
  const protectionLayers = [
    {
      icon: Fingerprint,
      title: "Identity Verification",
      description: "Every new account goes through our verification process. We verify social media profiles to ensure real people join our platform.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Bot,
      title: "AI-Powered Detection",
      description: "Our advanced fraud detection system analyzes signup patterns, device fingerprints, and behavior to identify suspicious activity in real-time.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Eye,
      title: "Continuous Monitoring",
      description: "We monitor all account activity 24/7. Unusual patterns trigger automatic reviews to protect our community from bad actors.",
      color: "from-[#00B894] to-emerald-500"
    },
    {
      icon: Scale,
      title: "Fair Play Enforcement",
      description: "Strict policies ensure everyone plays by the rules. Violations result in immediate account suspension and forfeiture of earnings.",
      color: "from-orange-500 to-red-500"
    }
  ];

  const whatWeDetect = [
    {
      threat: "Fake Accounts",
      description: "Automated bots and fake profiles trying to game the system",
      protection: "Social verification + AI detection"
    },
    {
      threat: "Multiple Accounts",
      description: "Users creating multiple accounts to earn extra rewards",
      protection: "Device fingerprinting + email verification"
    },
    {
      threat: "VPN/Proxy Abuse",
      description: "Hiding real location to bypass restrictions",
      protection: "IP quality scoring + location verification"
    },
    {
      threat: "Click Fraud",
      description: "Automated clicking or invalid task completions",
      protection: "Behavioral analysis + completion verification"
    },
    {
      threat: "Identity Fraud",
      description: "Using stolen identities or fake information",
      protection: "Social profile verification + manual review"
    }
  ];

  const legitimacyPoints = [
    {
      icon: Award,
      title: "Established Platform",
      description: "GigsEasy has been operating since 2020, building trust with thousands of members worldwide."
    },
    {
      icon: Users,
      title: "Real Community",
      description: "Our members are real people earning real money. Join our social channels to connect with other Gigsters."
    },
    {
      icon: Globe,
      title: "Global Presence",
      description: "Members from over 190 countries trust GigsEasy. We're a legitimate international platform."
    },
    {
      icon: BadgeCheck,
      title: "Verified Payments",
      description: "We use PayPal for secure, traceable payments. Real money, real withdrawals, real proof."
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: "You Sign Up",
      description: "Create an account with your email and social profile",
      status: "Your information is securely collected"
    },
    {
      step: 2,
      title: "Automatic Screening",
      description: "Our system analyzes your signup for risk factors",
      status: "AI checks for known fraud patterns"
    },
    {
      step: 3,
      title: "Risk Scoring",
      description: "You receive a trust score based on multiple factors",
      status: "Low-risk accounts are approved quickly"
    },
    {
      step: 4,
      title: "Ongoing Protection",
      description: "Your account is continuously monitored for security",
      status: "Suspicious activity triggers review"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <Link to="/register">
            <Button className="bg-[#00B894] hover:bg-[#00A085] text-white">
              Join Safely
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-white via-[#00B894]/5 to-[#0984E3]/5">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#00B894]/10 text-[#00B894] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <ShieldCheck className="w-4 h-4" />
            Protecting our community
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Our Fraud{" "}
            <span className="bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
              Prevention System
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            We invest heavily in fraud prevention to ensure GigsEasy remains a safe, 
            fair, and legitimate platform for everyone. Here's how we protect you.
          </p>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="py-8 px-6 bg-[#00B894]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            <div>
              <p className="text-3xl font-bold mb-1">99.7%</p>
              <p className="text-white/80 text-sm">Fraud Detection Rate</p>
            </div>
            <div>
              <p className="text-3xl font-bold mb-1">24/7</p>
              <p className="text-white/80 text-sm">System Monitoring</p>
            </div>
            <div>
              <p className="text-3xl font-bold mb-1">&lt;1%</p>
              <p className="text-white/80 text-sm">Fraudulent Accounts</p>
            </div>
            <div>
              <p className="text-3xl font-bold mb-1">Real-time</p>
              <p className="text-white/80 text-sm">Threat Detection</p>
            </div>
          </div>
        </div>
      </section>

      {/* Protection Layers */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Multi-Layer Protection
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our fraud prevention system uses multiple overlapping layers to catch threats
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {protectionLayers.map((layer, index) => (
              <div key={index} className="relative bg-slate-50 rounded-2xl p-8 overflow-hidden">
                <div className={`absolute top-0 left-0 w-2 h-full bg-gradient-to-b ${layer.color}`} />
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${layer.color} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                    <layer.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{layer.title}</h3>
                    <p className="text-slate-600">{layer.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Detect */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h2 className="text-3xl font-bold mb-4">
              Threats We Protect Against
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Our system is designed to detect and prevent various types of fraud
            </p>
          </div>

          <div className="space-y-4">
            {whatWeDetect.map((item, index) => (
              <div key={index} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="md:w-1/4 flex items-center gap-3">
                    <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                    <span className="font-semibold text-red-300">{item.threat}</span>
                  </div>
                  <div className="md:w-2/5 text-slate-400 text-sm">
                    {item.description}
                  </div>
                  <div className="md:w-1/3 flex items-center gap-2 bg-[#00B894]/20 text-[#00B894] px-4 py-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">{item.protection}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              How Our System Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From signup to ongoing protection, here's the journey
            </p>
          </div>

          <div className="relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-slate-200 -translate-y-1/2" />
            
            <div className="grid md:grid-cols-4 gap-8 relative">
              {howItWorks.map((step, index) => (
                <div key={index} className="text-center relative bg-white">
                  <div className="w-16 h-16 bg-[#00B894] rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl relative z-10">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm mb-3">{step.description}</p>
                  <p className="text-xs text-[#00B894] bg-[#00B894]/10 px-3 py-1 rounded-full inline-block">
                    {step.status}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why We're Legitimate */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Why GigsEasy is Legitimate
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Wondering if GigsEasy is real? Here's why thousands trust us
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {legitimacyPoints.map((point, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-14 h-14 bg-[#00B894]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <point.icon className="w-7 h-7 text-[#00B894]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{point.title}</h3>
                <p className="text-slate-600 text-sm">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fair Play Notice */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-amber-50 border-2 border-amber-200 rounded-3xl p-8">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-10 h-10 text-amber-500 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-amber-800 mb-4">
                  Fair Play Policy
                </h2>
                <p className="text-amber-700 mb-4">
                  GigsEasy has a zero-tolerance policy for fraud. Attempting to game the system 
                  will result in immediate consequences:
                </p>
                <ul className="space-y-2 text-amber-700">
                  <li className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span>Permanent account suspension</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span>Forfeiture of all pending earnings</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span>Ban from future registration</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span>Potential legal action for serious violations</span>
                  </li>
                </ul>
                <p className="text-amber-600 mt-4 text-sm">
                  Play fair, and you'll have a great experience earning with GigsEasy!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#00B894]/10 to-[#0984E3]/10">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="w-16 h-16 text-[#00B894] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Join a Safe & Fair Platform
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Our fraud prevention measures mean you're joining a community of real people 
            earning real money. No bots, no scams, just legitimate opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-[#00B894] hover:bg-[#00A085] text-white px-8">
                Create Free Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/data-security">
              <Button size="lg" variant="outline" className="border-slate-300">
                Learn About Data Security
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
